'use client';

import { Box, Typography, Paper, Stack, Chip } from '@mui/material';
import { lusitana } from '@/components/app/proyectos/raceway/fonts';

type FuncionInfo = {
  id: number;
  titulo: string;
  utilidad: string;
  aplicaciones: string[];
  icono: string;
  gradiente: string;
  acento: string;
  tag: string;
};

type FuncionInfoCardProps = {
  fn: FuncionInfo;
  index: number;
};

// Mapeo de utilidades de Tailwind a valores CSS
const tailwindMap: Record<string, string> = {
  // Acentos (Bordes izquierdos)
  'border-blue-600': '#2563eb',
  'border-blue-500': '#3b82f6',
  'border-sky-500': '#0ea5e9',
  // Gradientes
  'from-white to-blue-50': 'linear-gradient(to right, #ffffff 0%, #eff6ff 100%)',
  'from-blue-50 to-white': 'linear-gradient(to right, #eff6ff 0%, #ffffff 100%)',
  // Tags
  'bg-blue-100 text-blue-700': '#dbeafe', // El color de fondo, el texto lo manejamos en el componente
};

export default function FuncionInfoCard({ fn, index }: FuncionInfoCardProps) {
  // Obtenemos los valores reales de CSS o usamos el valor por defecto si no está en el mapa
  const borderColor = tailwindMap[fn.acento] || '#3b82f6';
  const backgroundStyle = tailwindMap[fn.gradiente] || '#ffffff';
  const tagBg = tailwindMap[fn.tag] || '#eff6ff';

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'flex-start' },
        gap: { md: 3 },
        overflow: 'hidden',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        borderLeft: `4px solid ${borderColor}`,
        background: backgroundStyle,
        p: 2.5,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }
      }}
    >
      {/* Número decorativo */}
      <Typography
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          right: 20,
          top: 12,
          fontSize: '4.5rem',
          fontWeight: 'bold',
          color: 'rgba(30, 58, 138, 0.05)', // blue-900 con 0.05 opacidad
          userSelect: 'none',
          lineHeight: 1
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </Typography>

      {/* Icono */}
      <Box
        sx={{
          mb: { xs: 1.5, md: 0 },
          display: 'flex',
          height: 48,
          width: 48,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '12px',
          bgcolor: 'rgba(255, 255, 255, 0.6)',
          fontSize: '1.5rem',
          border: '1px solid #bfdbfe', // blue-200
          color: '#1d4ed8', // blue-700
        }}
      >
        {fn.icono}
      </Box>

      {/* Contenido */}
      <Box sx={{ flex: 1, zIndex: 1 }}>
        <Typography
          className={lusitana.className}
          sx={{
            fontSize: '1rem',
            fontWeight: 'bold',
            color: '#1d4ed8', // blue-700
            mb: 0.5
          }}
        >
          {fn.titulo}
        </Typography>
        
        <Typography
          variant="body2"
          sx={{
            color: '#4b5563', // gray-600
            lineHeight: 1.6,
            maxWidth: '90%'
          }}
        >
          {fn.utilidad}
        </Typography>

        {/* Tags / Aplicaciones */}
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {fn.aplicaciones.map((ap) => (
            <Chip
              key={ap}
              label={ap}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.75rem',
                fontWeight: 500,
                bgcolor: tagBg,
                color: '#1d4ed8',
                border: '1px solid #bfdbfe',
                borderRadius: '9999px',
              }}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
}